import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Stack,
    Typography,
    Grid,
    useTheme,
    InputAdornment,
} from '@mui/material';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useEffect, useState, useMemo } from 'react';
import eventEmitter from 'SnackBarEventEmitter';
import { saveJobSlaConfig } from 'service/service';
import CustomAutocomplete from 'components/Inputs/CustomAutocomplete';
import CustomTextField from 'components/Inputs/CustomTextField';
import { dayOptions, deadlineTypeOptions, ruleOptions, scheduleTypeOptions, slotOptions } from 'utils/common-util';
import { getAutocompleteCommonComponentsProps, getInputCustomCommonSx, getDisabledInputSx, getDropdownDisabledTextFieldSx } from './DataPipelineHelper';
import CustomTimePicker from 'components/Inputs/CustomTimePicker';
import OutlinedButton from 'components/Button/OutlinedButton';
import { Clear } from '@mui/icons-material';
import MultiSelectWithCheckbox from 'components/Dropdown/MultiSelectWithCheckbox';
import { CustomArrowIcon } from 'components/CommonIcons/Icons';

interface SLAConfigurationModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: any) => void;
    selectedProject: any;
    initialData: any;
    isViewOnly?: boolean;
}

const SLAConfigurationModal: React.FC<SLAConfigurationModalProps> = ({
    open,
    onClose,
    onSave,
    selectedProject,
    initialData,
    isViewOnly = false
}) => {
    const theme = useTheme();
    const colors = theme.palette;

    const inputCommonSx = {
        "& .MuiInputBase-input": {
            color: `${colors.input.text} !important`,
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: `${colors.inputNew.inputBorder} !important`,
        },
        "& .MuiInputLabel-root": {
            color: `${colors.input.text} !important`,
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: `${colors.input.text} !important`,
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: `${colors.inputNew.inputBorderFocus} !important`,
        },
        "& .MuiFormHelperText-root": {
            color: `${colors.input.text} !important`,
        },
        '& .MuiOutlinedInput-root.Mui-disabled .MuiInputBase-input': {
            WebkitTextFillColor: `${colors.input.text} !important`,
        },
        '& .MuiFormLabel-root.MuiInputLabel-root.Mui-disabled': {
            WebkitTextFillColor: `${colors.input.text} !important`,
        },
    };

    const commonCustomTextFieldProps = {
        InputLabelProps: {
            sx: {
                '&&': { color: `${colors.input.text} !important` },
                '&.Mui-focused': { color: `${colors.input.text} !important` },
                '&.MuiInputLabel-shrink': { color: `${colors.input.text} !important` },
            }
        },
        InputProps: {
            sx: {
                '&&': { color: `${colors.input.text} !important` },
                '& input': { color: `${colors.input.text} !important` }
            }
        },
        sx: {
            '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: `${colors.inputNew.inputBorder} !important` },
                '&:hover fieldset': { borderColor: `${colors.inputNew.inputBorderFocus} !important` },
                '&.Mui-focused fieldset': { borderColor: `${colors.inputNew.inputBorderFocus} !important` }
            },
           ...inputCommonSx
        }
    };

    const getModalDropdownEnabledTextFieldSx = () => ({
        '& .MuiInputBase-input:not(.Mui-disabled)': {
            color: '#000 !important',
            WebkitTextFillColor: '#000 !important'
        },
        '& input:not(.Mui-disabled)': {
            color: '#000 !important',
            WebkitTextFillColor: '#000 !important'
        },
        '& .MuiInputLabel-root:not(.Mui-disabled)': {
            color: '#000 !important',
        },
    });

    const slaTimeInputSx = {
        background: 'transparent',
        color: 'textLight.main',
        fontSize: '14px',
        fontWeight: 500,
        '& .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${colors.inputNew.inputBorder} !important`,
        },
        '&:hover .MuiOutlinedInput-notchedOutline, &.Mui-error .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `${colors.inputNew.inputBorderFocus} !important`,
        },
        '& .MuiSvgIcon-root': {
            color: '#009BD8'
        },
        '& input::placeholder': {
            color: 'textLight.main',
            opacity: 1
        }
    };

    const getSlaTimeInputProps = (params: any) => ({
        ...params.InputProps,
        sx: {
            ...slaTimeInputSx,
            '& .MuiInputAdornment-positionEnd': {
                pointerEvents: 'auto'
            }
        },
        inputProps: {
            ...params.inputProps,
            placeholder: 'Select Start Time'
        }
    });

    const getSlaEndTimeInputProps = (params: any) => ({
        ...params.InputProps,
        sx: {
            ...slaTimeInputSx,
            '& .MuiInputAdornment-positionEnd': {
                pointerEvents: 'auto'
            }
        },
        inputProps: {
            ...params.inputProps,
            placeholder: 'Select End Time'
        }
    });

    const emptyFormData = {
        scheduleType: '',
        deadlineType: '',
        rule: '',
        slotsAvailable: '',
        duration: '',
        slaStartTimes: [
            {
                id: 1,
                days: [],
                startTime: '',
                endTime: '',
                duration: '',
                criticality: true
            }
        ]
    };

    const MODAL_STATE_KEY = 'SLAConfigurationModalState';

    const ALL_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const cloneFormData = (data: any) => structuredClone(data || {});

    const getDerivedSlotsAvailable = (data: any) => {
        const deadlineType = String(data?.deadlineType || '').toLowerCase();
        if (deadlineType === 'clock') return 'Yes';
        if (deadlineType === 'interval') return 'No';
        return undefined;
    };

    const formatTimeForStorage = (time: any): string => {
        if (!time) return '';
        
        const timeStr = String(time).trim();
        
        // If already in HH:MM format, return as-is
        if (/^\d{2}:\d{2}$/.test(timeStr)) {
            return timeStr;
        }
        
        // Extract only digits
        const digits = timeStr.replace(/\D/g, '');
        
        // Normalize to exactly 4 digits
        let normalized = digits;
        if (digits.length < 4) {
            normalized = digits.padStart(4, '0');
        } else if (digits.length > 4) {
            // Take only the first 4 digits
            normalized = digits.substring(0, 4);
        }
        
        // Format as HH:MM
        return `${normalized.slice(0, 2)}:${normalized.slice(2, 4)}`;
    };

    const normalizeDaysValue = (value: any) => {
        let vals: string[] = [];
        if (Array.isArray(value)) vals = value;
        else if (typeof value === 'string' && value.length) vals = value.split(',').map((d: string) => d.trim()).filter(Boolean);

        if (vals.includes('All Days') || vals.length === ALL_DAYS.length) return [...ALL_DAYS];

        return ALL_DAYS.filter(d => vals.includes(d));
    };

    const normalizeRow = (row: any) => ({
        ...row,
        days: normalizeDaysValue(row.days),
        startTime: String(row.startTime || '').trim(),
        endTime: String(row.endTime || '').trim(),
        duration: String(row.duration || '').trim(),
        criticality: Boolean(row.criticality)
    });

    const normalizeFormData = (data: any) => {
        const normalized = cloneFormData(data || emptyFormData);

        normalized.scheduleType = String(normalized.scheduleType || '').trim();
        normalized.deadlineType = String(normalized.deadlineType || '').trim();
        normalized.rule = String(normalized.rule || '').trim();
        normalized.slotsAvailable = String(normalized.slotsAvailable || getDerivedSlotsAvailable(normalized) || '').trim();
        normalized.duration = String(normalized.duration || '').trim();

        normalized.slaStartTimes = Array.isArray(normalized.slaStartTimes)
            ? normalized.slaStartTimes.map(normalizeRow)
            : [];

        if (String(normalized.scheduleType).toLowerCase() === 'daily') {
            normalized.slaStartTimes = normalized.slaStartTimes.map((row: any) => ({
                ...row,
                days: [...ALL_DAYS]
            }));
        }

        if (String(normalized.deadlineType).toLowerCase() === 'clock') {
            normalized.duration = '';
            normalized.slaStartTimes = normalized.slaStartTimes.map((row: any) => ({
                ...row,
                duration: ''
            }));
        }

        return normalized;
    };

    const normalizeFormDataForDiff = (data: any) => {
        const normalized = normalizeFormData(data);
        const isSlotConfigNotVisible =
            String(normalized.deadlineType).toLowerCase() === 'interval' &&
            String(normalized.slotsAvailable).toLowerCase() === 'no';

        if (isSlotConfigNotVisible) {
            return {
                ...normalized,
                slaStartTimes: [],
            };
        }

        return normalized;
    };

    const getInitialFormData = () => normalizeFormData(initialData || emptyFormData);

    const [formData, setFormData] = useState<any>(getInitialFormData());
    const [initialFormData, setInitialFormData] = useState<any>(getInitialFormData());
    const [savedWeeklySlaStartTimes, setSavedWeeklySlaStartTimes] = useState<any[] | null>(null);

    useEffect(() => {
        if (!formData) return;

        const normalized = normalizeFormData(formData);
        if (JSON.stringify(normalized) !== JSON.stringify(formData)) {
            setFormData(normalized);
        }
    }, [formData.scheduleType, formData.deadlineType, formData.slotsAvailable, formData.duration, formData.slaStartTimes]);

    const loadPersistedModalState = () => {
        try {
            const saved = sessionStorage.getItem(MODAL_STATE_KEY);
            if (!saved) return null;
            return JSON.parse(saved);
        } catch (e) {
            console.error('Failed to load SLA modal state', e);
            return null;
        }
    };

    const persistModalState = (state: any) => {
        try {
            sessionStorage.setItem(MODAL_STATE_KEY, JSON.stringify(state));
        } catch (e) {
            console.error('Unable to persist SLA modal state', e);
        }
    };

    useEffect(() => {
        if (!open) return;

        const baselineFormData = getInitialFormData();

        if (isViewOnly) {
            setFormData(baselineFormData);
            setInitialFormData(baselineFormData);
            setSavedWeeklySlaStartTimes(null);
            return;
        }

        const saved = loadPersistedModalState();
        const currentProjectId = selectedProject?.Project_Id ?? selectedProject?.projectId ?? null;
        const currentJobId = selectedProject?.jobId ?? selectedProject?.Job_Id ?? null;

        if (saved?.open && saved?.selectedProject && !saved.viewOnly) {
            const savedProjectId = saved.selectedProject?.Project_Id ?? saved.selectedProject?.projectId ?? null;
            const savedJobId = saved.selectedProject?.jobId ?? saved.selectedProject?.Job_Id ?? null;

            if (savedProjectId === currentProjectId && savedJobId === currentJobId) {
                setFormData(saved.formData || baselineFormData);
                setInitialFormData(baselineFormData); // keep the TRUE baseline for diffing
                setSavedWeeklySlaStartTimes(null);
                return;
            }
        }

        setFormData(baselineFormData);
        setInitialFormData(baselineFormData);
        setSavedWeeklySlaStartTimes(null);
    }, [open, initialData, selectedProject, isViewOnly]);

    useEffect(() => {
        if (!open) return;
        if (isViewOnly) return;
        const persistedState = {
            open,
            selectedProject,
            initialData,
            formData,
            viewOnly: isViewOnly
        };
        persistModalState(persistedState);
    }, [open, selectedProject, initialData, formData, isViewOnly]);

    // When schedule type is Daily, force all SLA rows' days to All Days and prevent editing
    useEffect(() => {
        if (!formData) return;
        if (String(formData.scheduleType).toLowerCase() === 'daily') {
            const updated = (formData.slaStartTimes || []).map((r: any) => {
                const currentDays = Array.isArray(r.days) ? r.days : [];
                const isAllDays = currentDays.length === ALL_DAYS.length && ALL_DAYS.every((day) => currentDays.includes(day));
                return isAllDays ? r : { ...r, days: [...ALL_DAYS] };
            });
            if (JSON.stringify(updated) !== JSON.stringify(formData.slaStartTimes)) {
                setFormData({ ...formData, slaStartTimes: updated });
            }
        }
    }, [formData?.scheduleType, formData?.slotsAvailable, formData?.slaStartTimes]);

    const handleModalClose = () => {
        setFormData(getInitialFormData());
        // Clear any saved weekly selections when modal is closed to avoid leaking selections
        setSavedWeeklySlaStartTimes(null);
        onClose();
    };

    const handleTimeChange = (
        rowId: number,
        field: 'startTime' | 'endTime',
        dateOrStr: any
    ) => {
        let formattedTime = '';

        if (typeof dateOrStr === 'string') {
            formattedTime = dateOrStr;
        } else if (
            dateOrStr &&
            typeof dateOrStr === 'object' &&
            typeof dateOrStr.hour === 'function'
        ) {
            const hours = dateOrStr.hour().toString().padStart(2, '0');
            const minutes = dateOrStr.minute().toString().padStart(2, '0');
            formattedTime = `${hours}:${minutes}`;
        } else if (
            dateOrStr instanceof Date &&
            !Number.isNaN(dateOrStr.getTime())
        ) {
            const hours = dateOrStr.getHours().toString().padStart(2, '0');
            const minutes = dateOrStr.getMinutes().toString().padStart(2, '0');
            formattedTime = `${hours}:${minutes}`;
        }

        handleUpdateSLATimeRow(rowId, field, formattedTime);
    };

    const timeStringToDate = (time?: string) => {
        if (!time) return null;

        const [hours, minutes] = time.split(':').map(Number);

        const date = new Date();
        date.setHours(hours || 0);
        date.setMinutes(minutes || 0);
        date.setSeconds(0);

        return date;
    };

    const [saving, setSaving] = useState(false);

    const hasFormChanges = useMemo(() => {
        if (!formData || !initialFormData) return false;

        const normalizedCurrent = normalizeFormDataForDiff(formData);
        const normalizedInitial = normalizeFormDataForDiff(initialFormData);

        return JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedInitial);
    }, [formData, initialFormData]);

    const isFormValid = useMemo(() => {
        if (!formData) return false;
        const required = ['scheduleType', 'deadlineType', 'rule', 'slotsAvailable'];
        for (const key of required) {
            if (!formData[key] || String(formData[key]).trim() === '') return false;
        }

        const isInterval = String(formData.deadlineType).toLowerCase() === 'interval';
        const isSlotConfigNotVisable =
            isInterval &&
            String(formData.slotsAvailable).toLowerCase() === 'no';

        if (isSlotConfigNotVisable) {
            // duration must be present and greater than 0 minutes
            if (!formData.duration || String(formData.duration).trim() === '') return false;
            const overallDuration = typeof formData.duration === 'string' ? parseInt(formData.duration.replace(/\D/g, '')) || 0 : Number(formData.duration) || 0;
            if (overallDuration <= 0) return false;
            return true;
        }

        if (!Array.isArray(formData.slaStartTimes) || formData.slaStartTimes.length === 0) return false;

        for (const row of formData.slaStartTimes) {
            const daysArr = Array.isArray(row.days) ? row.days : (typeof row.days === 'string' ? row.days.split(',').map((d: string) => d.trim()).filter(Boolean) : []);
            if (daysArr.length === 0) return false;
            if (!row.startTime || String(row.startTime).trim() === '') return false;
            if (isInterval) {
                if (!row.duration || String(row.duration).trim() === '') return false;
                const rowDur = typeof row.duration === 'string' ? parseInt(row.duration.replace(/\D/g, '')) || 0 : Number(row.duration) || 0;
                if (rowDur <= 0) return false;
            } else {
                if (!row.endTime || String(row.endTime).trim() === '') return false;
            }
        }

        return true;
    }, [formData]);

    const isSlotConfigNotVisable =
        String(formData.deadlineType).toLowerCase() === 'interval' &&
        String(formData.slotsAvailable).toLowerCase() === 'no';

    const loadText = formData?.isNew ? 'Saving...' : 'Updating...';
    const buttonText = formData?.isNew ? 'Save' : 'Update';
    const actionButtonLabel = saving ? (loadText) : (buttonText);

    const handleSave = async () => {
        if (isViewOnly || !isFormValid) return;
        setSaving(true);
        const projectId = selectedProject?.projectId ?? (selectedProject?.Project_Id && selectedProject?.Project_Id !== 0 ? selectedProject.Project_Id : null);
        const projectName = selectedProject?.Project_Name ?? selectedProject?.projectName ?? '';
        const jobId = selectedProject?.jobId ?? selectedProject?.Job_Id ?? null;
        const jobName = selectedProject?.jobName ?? '';

        // Build payload matching backend sample structure
        const programId = selectedProject?.programId ?? null;
        const programName = selectedProject?.programName ?? '';
        const platformName = selectedProject?.platformName ?? '';
        const schedulerName = selectedProject?.schedulerName ?? '';


        const slots: Record<string, Record<string, any>> = {};
        const dayNameToIndex: Record<string, string> = {
            Sun: '0',
            Mon: '1',
            Tue: '2',
            Wed: '3',
            Thu: '4',
            Fri: '5',
            Sat: '6'
        };
        const dayRowCounters: Record<string, number> = {};
        const shouldUseEmptySlots = isSlotConfigNotVisable;

        if (!shouldUseEmptySlots) {
            (formData.slaStartTimes || []).forEach((r: any) => {
                const normalizedDays = normalizeDaysValue(r.days);
                const isCriticalValue = r.criticality ? 'Y' : 'N';
                const durationVal = typeof r.duration === 'string' ? parseInt(r.duration.replace(/\D/g, '')) || 0 : Number(r.duration) || 0;

                normalizedDays.forEach((day: string) => {
                    const dayIndex = dayNameToIndex[day];
                    if (dayIndex === undefined) return;

                    if (!slots[dayIndex]) {
                        slots[dayIndex] = {};
                        dayRowCounters[dayIndex] = 0;
                    }

                    const rowPosition = String(++dayRowCounters[dayIndex]);

                    if (String(formData.deadlineType).toLowerCase() === 'interval') {
                        slots[dayIndex][rowPosition] = {
                            startTime: formatTimeForStorage(r.startTime) || '',
                            isCritical: isCriticalValue,
                            duration: durationVal
                        };
                    } else {
                        const start = formatTimeForStorage(r.startTime) || '';
                        const end = formatTimeForStorage(r.endTime) || '';
                        let endDayOffset = 0;
                        if (start && end && typeof start === 'string' && typeof end === 'string') {
                            const [sh, sm] = start.split(':').map((v: string) => parseInt(v || '0', 10));
                            const [eh, em] = end.split(':').map((v: string) => parseInt(v || '0', 10));
                            const startMinutes = (Number.isNaN(sh) ? 0 : sh) * 60 + (Number.isNaN(sm) ? 0 : sm);
                            const endMinutes = (Number.isNaN(eh) ? 0 : eh) * 60 + (Number.isNaN(em) ? 0 : em);
                            if (endMinutes < startMinutes) endDayOffset = 1;
                        }
                        slots[dayIndex][rowPosition] = {
                            startTime: start,
                            endTime: end,
                            endDayOffset,
                            isCritical: isCriticalValue
                        };
                    }
                });
            });
        }

        const deadlineObj: any = {
            type: String(formData.deadlineType).toUpperCase() === 'CLOCK' ? 'CLOCK' : 'INTERVAL',
            rule: formData.rule || '',
            slots: slots
        };

        const slotsAvailableRaw = formData.slotsAvailable;
        let slottingValue: boolean | undefined;
        if (slotsAvailableRaw !== undefined && slotsAvailableRaw !== null) {
            if (typeof slotsAvailableRaw === 'string') {
                const low = slotsAvailableRaw.toLowerCase();
                if (low === 'yes' || low === '1' || low === 'true') slottingValue = true;
                else if (low === 'no' || low === '0' || low === 'false') slottingValue = false;
                else slottingValue = Boolean(slotsAvailableRaw);
            } else if (typeof slotsAvailableRaw === 'number') {
                slottingValue = slotsAvailableRaw !== 0;
            } else if (typeof slotsAvailableRaw === 'boolean') {
                slottingValue = slotsAvailableRaw;
            } else {
                slottingValue = Boolean(slotsAvailableRaw);
            }
        }

        if (slottingValue !== undefined) {
            deadlineObj.slotting = slottingValue;
        }

        if (String(formData.deadlineType).toLowerCase() === 'interval' && formData.duration) {
            const durationVal = typeof formData.duration === 'string' ? parseInt(formData.duration.replace(/\D/g, '')) || 0 : Number(formData.duration) || 0;
            deadlineObj.duration = durationVal;
        }

        const payload = {
            programId,
            programName,
            projectId,
            projectName,
            jobId,
            jobName,
            platformName,
            schedulerName,
            scheduleType: String(formData.scheduleType),
            deadline: deadlineObj
        };

        try {
            const res = await saveJobSlaConfig(payload);
            setSaving(false);
            if (res?.success) {
                const message = res.data?.message || 'SLA saved successfully';
                eventEmitter.emit('showSnackbar', message, 'success');
                onSave(formData);
                handleModalClose();
            } else {
                eventEmitter.emit('showSnackbar', 'Failed to save SLA', 'error');
            }
        } catch (err: any) {
            console.error('Error saving job SLA configuration', err);
            eventEmitter.emit('showSnackbar', err?.message || 'Failed to save SLA', 'error');
            setSaving(false);
        }
    };

    const handleAddSLATimeRow = () => {
        if (isViewOnly) return;
        const newId =
            Math.max(
                ...formData.slaStartTimes.map(
                    (r: any) => r.id
                ),
                0
            ) + 1;

        const isDaily = String(formData.scheduleType).toLowerCase() === 'daily';
        setFormData({
            ...formData,
            slaStartTimes: [
                ...formData.slaStartTimes,
                {
                    id: newId,
                    days: isDaily ? [...ALL_DAYS] : [],
                    startTime: '',
                    endTime: '',
                    duration: '',
                    criticality: true
                }
            ]
        });
    };

    const handleRemoveSLATimeRow = (id: number) => {
        setFormData({
            ...formData,
            slaStartTimes: formData.slaStartTimes.filter((row: any) => row.id !== id)
        });
    };

    const handleScheduleTypeChange = (value: any) => {
        if (isViewOnly) return;

        const newSchedule = value;

        // Switching TO Daily: if currently Weekly, save the current weekly selections so we can restore later
        if (String(newSchedule).toLowerCase() === 'daily') {
            if (String(formData.scheduleType).toLowerCase() === 'weekly') {
                const snapshot = (formData.slaStartTimes || []).map((r: any) => ({ ...r, days: Array.isArray(r.days) ? [...r.days] : [] }));
                setSavedWeeklySlaStartTimes(snapshot);
            }

            // Ensure Daily forces All Days (useEffect also enforces this but set explicitly for immediate UX)
            const dailyRows = (formData.slaStartTimes || []).map((r: any) => ({ ...r, days: [...ALL_DAYS] }));
            setFormData({ ...formData, scheduleType: newSchedule, slaStartTimes: dailyRows });
            return;
        }

        // Switching TO Weekly: restore saved weekly selections if available (or initial data if it was weekly)
        if (String(newSchedule).toLowerCase() === 'weekly') {
            const currentRows = formData.slaStartTimes || [];

            let restored: any[] | null = null;

            if (savedWeeklySlaStartTimes && savedWeeklySlaStartTimes.length > 0) {
                restored = currentRows.map((row: any, idx: number) => ({ ...row, days: savedWeeklySlaStartTimes[idx]?.days ? [...savedWeeklySlaStartTimes[idx].days] : [] }));
            } else if (initialFormData && String(initialFormData.scheduleType).toLowerCase() === 'weekly' && Array.isArray(initialFormData.slaStartTimes)) {
                restored = currentRows.map((row: any, idx: number) => ({ ...row, days: initialFormData.slaStartTimes[idx]?.days ? [...initialFormData.slaStartTimes[idx].days] : [] }));
            } else {
                // No prior weekly data available — keep empty selection so user can pick
                restored = currentRows.map((row: any) => ({ ...row, days: [] }));
            }

            setFormData({ ...formData, scheduleType: newSchedule, slaStartTimes: restored });
            return;
        }

        // Fallback: just set the schedule type
        setFormData({ ...formData, scheduleType: newSchedule });
    };

    const handleUpdateSLATimeRow = (id: number, field: string, value: any) => {
        const updatedRows = formData.slaStartTimes.map((row: any) => {
            if (row.id !== id) return row;
            if (field === 'days') {
                const normalized = normalizeDaysValue(value);
                return { ...row, days: normalized };
            }
            
            let newValue = value;
            if (field === 'startTime' || field === 'endTime') {
                if (value && typeof value === 'object' && typeof value.getHours === 'function') {
                    // JS Date
                    const hours = value.getHours().toString().padStart(2, '0');
                    const minutes = value.getMinutes().toString().padStart(2, '0');
                    newValue = `${hours}:${minutes}`;
                } else if (value && typeof value === 'object' && typeof value.hour === 'function') {
                    // Dayjs
                    const hours = value.hour().toString().padStart(2, '0');
                    const minutes = value.minute().toString().padStart(2, '0');
                    newValue = `${hours}:${minutes}`;
                } else if (typeof value === 'string') {
                    newValue = value;
                } else {
                    newValue = '';
                }
            }
            return { ...row, [field]: newValue };
        });
        setFormData({ ...formData, slaStartTimes: updatedRows });
    };

    return (
        <Dialog
            open={open}
            onClose={handleModalClose}
            maxWidth='lg'
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: '8px',
                    backgroundColor: 'backgroundLight.primary',
                    maxWidth: 'calc(100vw - 32px)',
                    width: '900px',
                }
            }}
        >
            <DialogTitle
                sx={{
                    backgroundColor: '#003366',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '16px',
                    padding: '16px 20px'
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>{isViewOnly ? 'View SLA For' : 'Configure SLA For'} {selectedProject?.Project_Name}</Box>
                    <IconButton
                        onClick={handleModalClose}
                        sx={{
                            color: '#fff',
                            padding: '4px',
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.08)'
                            }
                        }}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ padding: '24px', maxHeight: '70vh', overflowY: 'auto' }}>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    {/* Basic Configuration Row */}
                    <Stack direction="row" spacing={1}>
                       <CustomTextField
                            id="selected-job"
                            label={
                                <Box display="flex" alignItems="center">
                                    Selected Job
                                </Box>
                            }
                            value={selectedProject?.jobName || ''}
                            disabled
                            sx={{
                                ...inputCommonSx,
                                '& .MuiOutlinedInput-root.Mui-disabled': {
                                    backgroundColor: '#71758F0D !important',
                                    color: '#71758F !important',
                                    '& fieldset': {
                                        borderColor: '#e0e0e0 !important',
                                    }
                                },
                                '& .MuiOutlinedInput-root.Mui-disabled .MuiInputBase-input': {
                                    color: '#71758F !important',
                                    WebkitTextFillColor: '#71758F !important',
                                },
                                '& .MuiFormLabel-root.Mui-disabled': {
                                    color: '#71758F !important',
                                },
                                '& .MuiFormLabel-root.MuiInputLabel-root.Mui-disabled': {
                                    color: '#71758F !important',
                                },
                                '& .MuiOutlinedInput-notchedOutline legend': {
                                    overflow: 'auto',
                                },
                            }}
                        />
                    </Stack>

                    <Stack direction="row" spacing={2} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                        <CustomAutocomplete
                            options={scheduleTypeOptions}
                            value={formData.scheduleType}
                            onChange={(e, value) => handleScheduleTypeChange(value)}
                            textFieldLabel="Schedule Type"
                            sx={{
                                ...getInputCustomCommonSx(colors),
                                ...getDisabledInputSx(),
                                '& .MuiOutlinedInput-notchedOutline legend': {
                                    overflow: 'auto',
                                },
                            }}
                            popupIcon={<CustomArrowIcon color={isViewOnly  ? '#71758F' : '#009BD8'} />}
                            componentsProps={getAutocompleteCommonComponentsProps(colors)}
                            customTextFieldProps={{
                                ...commonCustomTextFieldProps,
                                sx: {
                                    ...commonCustomTextFieldProps.sx,
                                    ...getDropdownDisabledTextFieldSx()
                                }
                            }}
                            disabled={isViewOnly}
                        />

                        <CustomAutocomplete
                            options={deadlineTypeOptions}
                            value={formData.deadlineType}
                            onChange={(e, value) => {
                                const updatedData = {
                                    ...formData,
                                    deadlineType: value
                                };
                                // Auto-select "Yes" for Slots Available when deadline type is "Clock"
                                if (value === 'Clock') {
                                    updatedData.slotsAvailable = 'Yes';
                                } else if (value === 'Interval') {
                                    // Auto-select "No" for Slots Available when deadline type is "Interval"
                                    updatedData.slotsAvailable = 'No';
                                }
                                setFormData(updatedData);
                            }}
                            textFieldLabel="Deadline Type"
                            popupIcon={<CustomArrowIcon color={isViewOnly  ? '#71758F' : '#009BD8'} />}
                            sx={{
                                ...getInputCustomCommonSx(colors),
                                ...getDisabledInputSx(),
                                '& .MuiOutlinedInput-notchedOutline legend': {
                                    overflow: 'auto',
                                },
                            }}
                            componentsProps={getAutocompleteCommonComponentsProps(colors)}
                            customTextFieldProps={{
                                ...commonCustomTextFieldProps,
                                sx: {
                                    ...commonCustomTextFieldProps.sx,
                                    ...getDropdownDisabledTextFieldSx()
                                }
                            }}
                            disabled={isViewOnly}
                        />
                    </Stack>
                    <Stack direction="row" spacing={2} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                        <CustomAutocomplete
                            options={ruleOptions}
                            value={formData.rule}
                            onChange={(e, value) =>
                                setFormData({
                                    ...formData,
                                    rule: value
                                })
                            }
                            textFieldLabel="Rule"
                            popupIcon={<CustomArrowIcon color={isViewOnly  ? '#71758F' : '#009BD8'} />}
                            sx={{
                                ...getInputCustomCommonSx(colors),
                                ...getDisabledInputSx(),
                                '& .MuiOutlinedInput-notchedOutline legend': {
                                    overflow: 'auto',
                                },
                            }}
                            componentsProps={getAutocompleteCommonComponentsProps(colors)}
                            customTextFieldProps={{
                                ...commonCustomTextFieldProps,
                                sx: {
                                    ...commonCustomTextFieldProps.sx,
                                    ...getDropdownDisabledTextFieldSx()
                                }
                            }}
                            disabled={isViewOnly}
                        />
                        <CustomAutocomplete
                            options={slotOptions}
                            value={formData.slotsAvailable}
                            onChange={(e, value) =>
                                setFormData({
                                    ...formData,
                                    slotsAvailable: value
                                })
                            }
                            disabled={isViewOnly || String(formData.deadlineType).toLowerCase() === 'clock'}
                            textFieldLabel="Slots Available"
                            popupIcon={<CustomArrowIcon color={isViewOnly || String(formData.deadlineType).toLowerCase() === 'clock' ? '#71758F' : '#009BD8'} />}
                            sx={{
                                ...getInputCustomCommonSx(colors),
                                ...getDisabledInputSx(),
                                '& .MuiOutlinedInput-notchedOutline legend': {
                                    overflow: 'auto',
                                },
                            }}
                            componentsProps={getAutocompleteCommonComponentsProps(colors)}
                            customTextFieldProps={{
                                ...commonCustomTextFieldProps,
                                sx: {
                                    ...commonCustomTextFieldProps.sx,
                                    ...getDropdownDisabledTextFieldSx()
                                }
                            }}
                        />
                    </Stack>

                    {formData.deadlineType === 'Interval' && formData.slotsAvailable === 'No' && (
                        <Stack direction="row" spacing={2} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                            <CustomTextField
                                id="duration"
                                label="Duration"
                                value={formData.duration || ''}
                                onChange={(e) => {
                                    if (isViewOnly) return;
                                    const value = e.target.value.replace(/\D/g, ''); // Remove non-numeric characters
                                    setFormData({
                                        ...formData,
                                        duration: value ? `${value} Mins` : ''
                                    });
                                }}
                                placeholder="Duration"
                                sx={{
                                    ...commonCustomTextFieldProps.sx,
                                    ...getDisabledInputSx(), 
                                    ...getModalDropdownEnabledTextFieldSx(),
                                    ...getDropdownDisabledTextFieldSx(),
                                    '& .MuiFormLabel-root.MuiInputLabel-root.Mui-disabled': {
                                        color: '#71758F !important'
                                    },
                                    '& .MuiOutlinedInput-notchedOutline legend': {
                                        overflow: 'auto',
                                    },
                                }}
                                InputProps={{
                                    sx: { ...getDisabledInputSx() },
                                    endAdornment: (
                                        <InputAdornment position="end" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '2px', mr: 1, alignItems: 'center' }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    if (isViewOnly) return;
                                                    const currentValue = parseInt(formData.duration) || 0;
                                                    const newValue = currentValue + 5;
                                                    setFormData({
                                                        ...formData,
                                                        duration: `${newValue} Mins`
                                                    });
                                                }}
                                                sx={{ padding: '2px', minWidth: 'auto', height: '24px', width: '24px', ...getDisabledInputSx() }}
                                                disabled={isViewOnly}
                                            >
                                                <KeyboardArrowUpIcon sx={{ fontSize: '18px', color: isViewOnly ? '#71758F' : '#009BD8' }} />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    if (isViewOnly) return;
                                                    const currentValue = parseInt(formData.duration) || 0;
                                                    const newValue = Math.max(0, currentValue - 5);
                                                    setFormData({
                                                        ...formData,
                                                        duration: newValue > 0 ? `${newValue} Mins` : ''
                                                    });
                                                }}
                                                sx={{ padding: '2px', minWidth: 'auto', height: '24px', width: '24px', ...getDisabledInputSx() }}
                                                disabled={isViewOnly}
                                            >
                                                <KeyboardArrowDownIcon sx={{ fontSize: '18px', color: isViewOnly ? '#71758F' : '#009BD8' }} />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                disabled={isViewOnly}
                            />
                        </Stack>
                    )}

                    <Box>
                        <Stack
                            direction="row"
                            alignItems="center"
                            sx={{ mb: 2 }}
                        >
                            <Typography
                                sx={{
                                    color: '#00A3E0',
                                    fontWeight: 600
                                }}
                            >
                                Configure SLA Start-End Time
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: '#009BD8', mx: 2 }} />
                            <Typography
                                sx={{
                                    color: '#00A3E0',
                                    fontWeight: 600
                                }}
                            >
                                SLA Criticality
                            </Typography>
                            <Box sx={{ width: '55px', height: '1px', bgcolor: '#009BD8', ml: 2 }} />
                        </Stack>

                        <Box sx={{ maxHeight: '200px', overflowY: 'auto', pr: 1, pt: 1, boxSizing: 'border-box' }}>
                        {formData.slaStartTimes.map(
                            (row: any, index: number) => (
                                <Grid
                                    key={row.id}
                                    container
                                    spacing={2}
                                    alignItems="center"
                                    sx={{ mb: 2 }}
                                >
                                    <Grid item xs={0.5}>
                                        <Typography
                                            sx={{
                                                width: 20,
                                                fontSize: '14px',
                                                color: '#000000',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {index + 1}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={2.5}>
                                           <MultiSelectWithCheckbox
                                            value={Array.isArray(row.days) ? row.days : []}
                                            options={dayOptions}
                                            placeholder="Select Days"
                                            label="Select Days"
                                            allOptionLabel="All Days"
                                            disabled={isViewOnly || String(formData.scheduleType).toLowerCase() === 'daily' || isSlotConfigNotVisable}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: `${colors.inputNew.inputBorder} !important`,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: `${colors.inputNew.inputBorderFocus} !important`,
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: `${colors.inputNew.inputBorderFocus} !important`,
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: `${colors.input.text} !important`,
                                                },
                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: `${colors.input.text} !important`,
                                                },
                                                '& .MuiOutlinedInput-notchedOutline legend': {
                                                    overflow: 'auto',
                                                },
                                                ...getDisabledInputSx(),
                                            }}
                                            onChange={(value) =>
                                                handleUpdateSLATimeRow(
                                                    row.id,
                                                    'days',
                                                    value
                                                )
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={3.2}>
                                        <Box
                                            sx={{
                                                '& .time-field-actions': {
                                                    opacity: 0,
                                                    pointerEvents: 'none',
                                                },
                                                '&:hover .time-field-actions, &:focus-within .time-field-actions': {
                                                    opacity: 1,
                                                    pointerEvents: 'auto',
                                                },
                                            }}
                                        >
                                            <CustomTimePicker
                                                ampm={false}
                                                value={timeStringToDate(row.startTime)}
                                                onChange={(value) => {
                                                    if (isViewOnly || isSlotConfigNotVisable) return;
                                                    handleTimeChange(row.id, 'startTime', value);
                                                }}
                                                disabled={isViewOnly || isSlotConfigNotVisable}
                                                readOnly={isViewOnly || isSlotConfigNotVisable}
                                                PopperProps={{
                                                    sx: {
                                                        '& .MuiPaper-root': { 
                                                            backgroundColor: '#fff !important',
                                                            position: 'relative !important', },
                                                        '& .MuiClockNumber-root': { color: '#000000' },
                                                        '& .MuiClockNumber-root.Mui-selected': {
                                                            backgroundColor: '#0095FF !important',
                                                            color: '#fff !important'
                                                        },
                                                        '& .MuiClockPointer-root': { backgroundColor: '#0095FF' },
                                                        '& .MuiClock-pin': { backgroundColor: '#0095FF' },
                                                        '& .MuiClockPointer-thumb': {
                                                            backgroundColor: '#0095FF !important',
                                                            border: '16px solid #0095FF !important'
                                                        },
                                                        '& .MuiSvgIcon-root': { color: '#009BD8 !important' },
                                                        '& .Mui-disabled .MuiSvgIcon-root': { color: '#BDBDBD !important' },
                                                    }
                                                }}
                                                renderInput={(params) => (
                                                <CustomTextField
                                                        {...params}
                                                        id={`start-time-${row.id}`}
                                                        disabled={isViewOnly || isSlotConfigNotVisable}
                                                        label={
                                                            <Box display="flex" alignItems="center">
                                                                SLA Start Time (CST)
                                                            </Box>
                                                        }
                                                        value={row.startTime || ''}
                                                        onChange={(e) => {
                                                            if (isViewOnly || isSlotConfigNotVisable) return;
                                                            handleUpdateSLATimeRow(row.id, 'startTime', e.target.value);
                                                        }}
                                                        InputProps={{
                                                            ...getSlaTimeInputProps(params),
                                                            endAdornment: (
                                                                <InputAdornment position="end" sx={{ display: 'flex', gap: 0.5 }} className="time-field-actions">
                                                                    <IconButton
                                                                        size="small"
                                                                        disabled={isViewOnly || isSlotConfigNotVisable}
                                                                        onClick={() =>
                                                                            handleUpdateSLATimeRow(row.id, 'startTime', '')
                                                                        }
                                                                        sx={{
                                                                            mr: -1,
                                                                            opacity: row.startTime && !isViewOnly && !isSlotConfigNotVisable ? 1 : 0,
                                                                            pointerEvents: row.startTime && !isViewOnly && !isSlotConfigNotVisable ? 'auto' : 'none',
                                                                            width: '32px',
                                                                            minWidth: '32px',
                                                                        }}
                                                                    >
                                                                        <ClearIcon sx={{ fontSize: '18px', color: isViewOnly || isSlotConfigNotVisable ? '#71758F !important' : '#000 !important' }} />
                                                                    </IconButton>
                                                                    {params.InputProps?.endAdornment}
                                                                </InputAdornment>
                                                            )
                                                        }}
                                                        sx={{
                                                            ...getInputCustomCommonSx(colors),
                                                            ...getDisabledInputSx(),
                                                            ...getModalDropdownEnabledTextFieldSx(),
                                                            '& .MuiOutlinedInput-notchedOutline legend': {
                                                                overflow: 'auto',
                                                            },
                                                            ...(isViewOnly || isSlotConfigNotVisable ? { '& input': { pointerEvents: 'none', caretColor: 'transparent' } } : {})
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Box>
                                    </Grid>

                                    <Grid item xs={3.2}>
                                        {String(formData.deadlineType).toLowerCase() === 'clock' ? (
                                            <Box
                                                sx={{
                                                    '& .time-field-actions': {
                                                        opacity: 0,
                                                        pointerEvents: 'none',
                                                    },
                                                    '&:hover .time-field-actions, &:focus-within .time-field-actions': {
                                                        opacity: 1,
                                                        pointerEvents: 'auto',
                                                    },
                                                }}
                                            >
                                                <CustomTimePicker
                                                    ampm={false}
                                                    value={timeStringToDate(row.endTime)}
                                                    onChange={(value) => {
                                                        if (isViewOnly || isSlotConfigNotVisable) return;
                                                        handleTimeChange(row.id, 'endTime', value);
                                                    }}
                                                    disabled={isViewOnly || isSlotConfigNotVisable}
                                                    readOnly={isViewOnly || isSlotConfigNotVisable}
                                                    PopperProps={{
                                                        sx: {
                                                            '& .MuiPaper-root': { 
                                                                backgroundColor: '#fff !important',
                                                                position: 'relative !important', 
                                                            },
                                                            '& .MuiClockNumber-root': {
                                                                color: '#000000'
                                                            },
                                                            '& .MuiClockNumber-root.Mui-selected': {
                                                                backgroundColor: '#0095FF !important',
                                                                color: '#fff !important'
                                                            },
                                                            '& .MuiClockPointer-root': {
                                                                backgroundColor: '#0095FF'
                                                            },
                                                            '& .MuiClock-pin': {
                                                                backgroundColor: '#0095FF'
                                                            },
                                                            '& .MuiClockPointer-thumb': {
                                                                backgroundColor: '#0095FF !important',
                                                                border: '16px solid #0095FF !important'
                                                            },
                                                            '& .MuiSvgIcon-root': {
                                                                color: '#009BD8 !important'
                                                            },
                                                            '& .Mui-disabled .MuiSvgIcon-root': {
                                                                color: '#BDBDBD !important'
                                                            }
                                                        }
                                                    }}
                                                    renderInput={(params) => (
                                                        <CustomTextField
                                                            {...params}
                                                            id={`end-time-${row.id}`}
                                                            label="SLA End Time (CST)"
                                                            value={row.endTime || ''}
                                                            disabled={isViewOnly || isSlotConfigNotVisable}
                                                            onChange={(e) => {
                                                                if (isViewOnly || isSlotConfigNotVisable) return;
                                                                handleUpdateSLATimeRow(row.id, 'endTime', e.target.value);
                                                            }}
                                                            InputProps={{
                                                                ...getSlaEndTimeInputProps(params),
                                                                endAdornment: (
                                                                    <InputAdornment position="end" sx={{ display: 'flex', gap: 0.5 }} className="time-field-actions">
                                                                        <IconButton
                                                                            size="small"
                                                                            disabled={isViewOnly || isSlotConfigNotVisable}
                                                                            onClick={() =>
                                                                                handleUpdateSLATimeRow(row.id, 'endTime', '')
                                                                            }
                                                                            sx={{
                                                                                mr: -1,
                                                                                opacity: row.endTime && !isViewOnly && !isSlotConfigNotVisable ? 1 : 0,
                                                                                pointerEvents: row.endTime && !isViewOnly && !isSlotConfigNotVisable ? 'auto' : 'none',
                                                                                width: '32px',
                                                                                minWidth: '32px',
                                                                            }}
                                                                        >
                                                                            <ClearIcon sx={{ fontSize: '18px', color: isViewOnly || isSlotConfigNotVisable ? '#71758F !important' : '#000 !important' }} />
                                                                        </IconButton>
                                                                        {params.InputProps?.endAdornment}
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                            sx={{
                                                                ...getDisabledInputSx(),
                                                                ...getModalDropdownEnabledTextFieldSx(),
                                                                '& .MuiOutlinedInput-notchedOutline legend': {
                                                                    overflow: 'auto',
                                                                },
                                                                ...(isViewOnly || isSlotConfigNotVisable ? { '& input': { pointerEvents: 'none', caretColor: 'transparent' } } : {})
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        ) : (
                                            <CustomTextField
                                                id={`duration-${row.id}`}
                                                label="Duration"
                                                value={row.duration || ''}
                                                onChange={(e) => {
                                                    if (isViewOnly || isSlotConfigNotVisable) return;
                                                    const value = e.target.value.replace(/\D/g, ''); // Remove non-numeric characters
                                                    handleUpdateSLATimeRow(
                                                        row.id,
                                                        'duration',
                                                        value ? `${value} Mins` : ''
                                                    );
                                                }}
                                                disabled={isViewOnly || isSlotConfigNotVisable}
                                                placeholder="Duration"
                                                sx={{
                                                    ...commonCustomTextFieldProps.sx,
                                                    ...getDisabledInputSx(), 
                                                    ...getModalDropdownEnabledTextFieldSx(),
                                                    '& .MuiFormLabel-root.MuiInputLabel-root.Mui-disabled': {
                                                        color: '#71758F !important'
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline legend': {
                                                        overflow: 'auto',
                                                    },
                                                }}
                                                InputProps={{
                                                    sx: { ...getDisabledInputSx() },
                                                    endAdornment: (
                                                        <InputAdornment position="end" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '2px', mr: 1, alignItems: 'center' }}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    const currentValue = parseInt(row.duration) || 0;
                                                                    const newValue = currentValue + 5;
                                                                    handleUpdateSLATimeRow(
                                                                        row.id,
                                                                        'duration',
                                                                        `${newValue} Mins`
                                                                    );
                                                                }}
                                                                sx={{ padding: '2px', minWidth: 'auto', height: '24px', width: '24px', ...getDisabledInputSx() }}
                                                            >
                                                                <KeyboardArrowUpIcon sx={{ fontSize: '18px', color: isViewOnly || isSlotConfigNotVisable ? '#71758F' : '#009BD8' }} />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    const currentValue = parseInt(row.duration) || 0;
                                                                    const newValue = Math.max(0, currentValue - 5);
                                                                    handleUpdateSLATimeRow(
                                                                        row.id,
                                                                        'duration',
                                                                        newValue > 0 ? `${newValue} Mins` : ''
                                                                    );
                                                                }}
                                                                sx={{ padding: '2px', minWidth: 'auto', height: '24px', width: '24px', ...getDisabledInputSx() }}
                                                            >
                                                                <KeyboardArrowDownIcon sx={{ fontSize: '18px', color: isViewOnly || isSlotConfigNotVisable ? '#71758F' : '#009BD8' }} />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        )}
                                    </Grid>
                                    <Grid item xs={1}>
                                        <Switch
                                            checked={row.criticality}
                                            onChange={(e) => {
                                                if (isViewOnly) return;
                                                handleUpdateSLATimeRow(
                                                    row.id,
                                                    'criticality',
                                                    e.target.checked
                                                );
                                            }}
                                            disabled={isViewOnly || isSlotConfigNotVisable}
                                            sx={{
                                                width: '48px',
                                                height: '28px',
                                                padding: '5px',
                                                marginLeft: 4.5,
                                                '& .MuiSwitch-switchBase': {
                                                    padding: '5px',
                                                    '&.Mui-checked': {
                                                        color: '#009BD8',
                                                        '& + .MuiSwitch-track': {
                                                            backgroundColor: '#009BD8',
                                                            opacity: 1,
                                                        },
                                                    },
                                                    '&.Mui-disabled': {
                                                        color: '#fff',
                                                        '& + .MuiSwitch-track': {
                                                            backgroundColor: '#A9ADCB !important',
                                                            opacity: '0.6 !important',
                                                        },
                                                    },
                                                },
                                                '& .MuiSwitch-thumb': {
                                                    width: '18px',
                                                    height: '18px',
                                                    backgroundColor: '#FFFFFF',
                                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                                },
                                                '& .MuiSwitch-track': {
                                                    backgroundColor: '#A9ADCB',
                                                    opacity: 1,
                                                    borderRadius: '14px',
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={1.2}>
                                        {index > 0 && !isViewOnly && (
                                            <OutlinedButton  
                                                sx={{ color: 'error.main',
                                                    borderColor: 'error.main', 
                                                    width: 50, height: 50, ml: 5,
                                                    '& .MuiSvgIcon-root': {
                                                        color: 'error.main',
                                                    }, 
                                                    }}
                                                    onClick={() => handleRemoveSLATimeRow(row.id)}
                                                >
                                                <Clear />
                                            </OutlinedButton>
                                        )}
                                    </Grid>
                                </Grid>
                            )
                        )}
                        </Box>

                        {!isViewOnly && (
                            <Button disabled = {isSlotConfigNotVisable}
                                onClick={handleAddSLATimeRow}
                                sx={{
                                    color: '#00A3E0',
                                    textTransform: 'none'
                                }}
                            >
                                + Add More
                            </Button>
                        )}
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'flex-end', gap: 1, px: 2, py: 1.5}}>
                {!isViewOnly && (
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        color="primary"
                        disabled={!isFormValid || !hasFormChanges || saving}
                        sx={{
                            textTransform: 'uppercase',
                            fontWeight: 600,
                            width: '10rem',
                            color: '#ffffff !important',
                            background: '#39ACE7 !important',
                            borderRadius: '8px !important',
                            '&.Mui-disabled': {
                                color: '#ffffff !important',
                                background: '#69c5f3ff !important',
                            }
                        }}
                    >
                        {actionButtonLabel}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default SLAConfigurationModal;
